import type { Identity } from '../types/auth';

/**
 * Scheduled command permission nodes, mirroring argus-backend `src/auth.ts`
 * (`scheduled_commands_*`).
 *
 * `write` is effectively console access on every server: a schedule can run
 * any command, on every server, forever. It implies `read`, and
 * `SPY_Super_admin` keeps the usual Argus override. The tellraw library and
 * the assistant sit behind the same two nodes.
 *
 * Client-side checks are for UX only; the backend verifies every request.
 */
export const SCHEDULED_COMMAND_PERMISSIONS = {
  read: 'argus.scheduledcommands.read',
  write: 'argus.scheduledcommands.write',
} as const;

export interface ScheduledCommandPerms {
  /** See schedules, their run history and the tellraw library. */
  canView: boolean;
  /** Create, change, run and delete schedules; use the assistant; edit the library. */
  canWrite: boolean;
}

function nodes(identity: Identity | null | undefined): string[] {
  const permissions = identity?.raw?.custom?.permissions;
  return Array.isArray(permissions) ? permissions : [];
}

function isSuper(identity: Identity | null | undefined): boolean {
  const groups = identity?.raw?.custom?.groups;
  return Array.isArray(groups) && groups.includes('SPY_Super_admin');
}

export function getScheduledCommandPerms(
  identity: Identity | null | undefined,
  isDev: boolean,
): ScheduledCommandPerms {
  if (isDev || isSuper(identity)) return { canView: true, canWrite: true };
  const held = nodes(identity);
  const canWrite = held.includes(SCHEDULED_COMMAND_PERMISSIONS.write);
  return { canView: canWrite || held.includes(SCHEDULED_COMMAND_PERMISSIONS.read), canWrite };
}

/** Shared by the route guard in App.tsx and the sidebar entry in navigation.ts. */
export const canViewScheduledCommands = (identity: Identity | null | undefined): boolean =>
  getScheduledCommandPerms(identity, false).canView;
